import { FunctionComponent, useCallback } from "react";

import { useTranslation } from "react-i18next";

import {
	faCheck,
	faClock,
	faClose,
	faDesktop,
	faEye,
	faFileCircleQuestion,
	faInfoCircle,
	faPlay,
	faPowerOff,
} from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import { Link } from "@mui/material";

import { runNowEligibility } from "@signalist/shared-types";

import CheckIcon from "@assets/icons/list-builder/checkGreen.svg?url";

import CalendarIcon from "@assets/icons/list-builder/calendarIcon.svg?url";

import { ListProcessingStatus, ListSourceType, ListStatus } from "@/enum/list.enum";
import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { List } from "../schema/list";
import { getListConfigurationStatus } from "../utils/useListConfigurationStatus";
import { listNextRunLabel } from "../utils/listScheduleLabels";
import { getTrackerStatusLabel } from "../utils/trackerStatusLabel";

/**
 * The hover text for a list whose "Run list now" the shared decision refuses (PREC-4537), or null
 * when the action may be offered. Keyed by the same reasons the API answers with.
 */
export const describeRunNowRefusal = (list: List, t: (key: string) => string): string | null => {
	const eligibility = runNowEligibility(list);

	if (eligibility.allowed) {
		return null;
	}

	switch (eligibility.reason) {
		case "LIST_ARCHIVED":
			return t("errors.listArchived");
		case "LIST_NOT_ACTIVE":
			return t("errors.listNotActive");
		case "LIST_NOT_IDLE":
			return t("errors.listNotIdle");
	}
};

interface ListProcessingStatusChipProps {
	list: List;
	processingStatus: ListProcessingStatus;
	overwriteText?: string;
	progressPercent?: number | null;
	lastActivity?: string | null;
	liveViewUrl?: string | null;
	status?: string | null;
	/** PREC-3624: force-run the list now (hover action on the "Scheduled for …" idle chip). */
	onRunNow?: (id: number) => void;
}

export const ListProcessingStatusChip: FunctionComponent<ListProcessingStatusChipProps> = ({
	list,
	processingStatus,
	overwriteText,
	progressPercent,
	lastActivity,
	liveViewUrl,
	status,
	onRunNow,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`list.${key}`), [t]);

	// PREC-3470: prefer the goal-based progress percentage; fall back to a stage
	// label (overwriteText) and then the generic status text.
	const runningText =
		progressPercent != null
			? `${progressPercent}%`
			: (overwriteText ?? ts(`status.${processingStatus}`));
	const config = getListConfigurationStatus(list);

	if (config.sourceMissing) {
		return (
			<Link href={config.url}>
				<div className="flex flex-row items-center text-xs font-semibold text-blue-action">
					<Icon className="mr-1 text-blue-action" icon={faFileCircleQuestion} />
					Add source
				</div>
			</Link>
		);
	}

	if (config.scrapingNotRequested) {
		return (
			<Link href={config.url}>
				<div className="flex flex-row items-center text-xs font-semibold text-blue-action">
					<Icon className="mr-1 text-blue-action" icon={faFileCircleQuestion} />
					Request scraping
				</div>
			</Link>
		);
	}

	if (config.scrapingNotConfirmed) {
		return (
			<Link href={config.url}>
				<div className="flex flex-row items-center text-xs font-semibold text-blue-action">
					<Icon className="mr-1 text-blue-action" icon={faCheck} />
					Confirm data
				</div>
			</Link>
		);
	}

	if (config.signalsMissing) {
		return (
			<Link href={config.url}>
				<div className="flex flex-row items-center text-xs font-semibold text-blue-action">
					<Icon className="mr-1 text-blue-action" icon={faFileCircleQuestion} />
					Configure signals
				</div>
			</Link>
		);
	}

	if (config.findPersonAtCompanyMissing) {
		return (
			<Link href={config.url}>
				<div className="flex flex-row items-center text-xs font-semibold text-blue-action">
					<Icon className="mr-1 text-blue-action" icon={faCheck} />
					Define ICP
				</div>
			</Link>
		);
	}

	if (config.scrapingOngoing) {
		return (
			<Link href={config.url}>
				<div className="flex flex-row items-center text-xs font-semibold text-status-running">
					<Icon className="mr-1 text-status-running" icon={faClock} />
					Scraping ongoing
				</div>
			</Link>
		);
	}

	if (status === ListStatus.STOPPING) {
		return (
			<div className="flex flex-row items-center text-xs font-semibold text-status-warning">
				<Icon className="mr-1 text-status-warning" icon={faClock} />
				{ts("status.STOPPING")}
			</div>
		);
	}

	if (status === ListStatus.PAUSED && processingStatus !== ListProcessingStatus.ERROR) {
		return (
			<div className="flex flex-row items-center text-xs font-semibold text-gray-700">
				<Icon className="mr-1 text-gray-700" icon={faPowerOff} />
				Deactivated
			</div>
		);
	}

	// PREC-3601: TRACKER lists monitor a fixed set of companies rather than fill toward a lead
	// goal, so a progress % is meaningless (it reads as a permanent 0%). Show what's being tracked
	// instead. Actionable config states (source/signals missing) are handled above, and genuine
	// error states fall through to the switch below, so both still take precedence.
	const isErrorStatus =
		processingStatus === ListProcessingStatus.ERROR ||
		processingStatus === ListProcessingStatus.NO_USER_FOUND ||
		processingStatus === ListProcessingStatus.NOT_ENOUGH_CREDITS;

	if (list.sourceType === ListSourceType.TRACKER && !isErrorStatus) {
		// An active tracker is continuously monitoring, so it uses the "running" treatment (amber +
		// eye) rather than a neutral/idle grey — it reads as a live status, not a deactivated one.
		return (
			<div className="flex flex-row items-center text-xs font-semibold text-status-running">
				<Icon className="mr-1 text-status-running" icon={faEye} />
				{getTrackerStatusLabel(t, list.trackerOrganizationCount)}
			</div>
		);
	}

	switch (processingStatus) {
		case ListProcessingStatus.ERROR:
		case ListProcessingStatus.NO_USER_FOUND:
		case ListProcessingStatus.NOT_ENOUGH_CREDITS: {
			const errorLabel = (
				<div className="flex flex-row items-center text-xs font-semibold text-status-error">
					<Icon className="mr-1 text-status-error" icon={faClose} />
					{ts(`status.${processingStatus}`)}
				</div>
			);

			// PREC-4240: "Error" alone told the user nothing; the run's reason is one hover away.
			return overwriteText ? <Tooltip content={overwriteText}>{errorLabel}</Tooltip> : errorLabel;
		}

		case ListProcessingStatus.PROCESSING:
		case ListProcessingStatus.API_WAITING:
			return lastActivity ? (
				<Tooltip
					content={`${ts("lastActivity")}: ${format(
						new Date(lastActivity),
						"HH:mm:ss dd.MM.yyyy"
					)}`}
				>
					<div className="flex flex-row items-center text-xs font-semibold text-status-running">
						<Icon className="mr-1 text-status-running" icon={faClock} />
						{runningText}
						{liveViewUrl && (
							<a
								className="cursor-pointer"
								href={liveViewUrl}
								rel="noopener noreferrer"
								target="_blank"
							>
								<Icon className="ml-1 text-status-running" icon={faDesktop} />
							</a>
						)}
					</div>
				</Tooltip>
			) : (
				<div className="flex flex-row items-center text-xs font-semibold text-status-running">
					<Icon className="mr-1 text-status-running" icon={faClock} />
					{runningText}
					{liveViewUrl && (
						<a
							className="cursor-pointer"
							href={liveViewUrl}
							rel="noopener noreferrer"
							target="_blank"
						>
							<Icon className="ml-1 text-status-running" icon={faDesktop} />
						</a>
					)}
				</div>
			);
		case ListProcessingStatus.DONE:
			if (list?.specialType !== null) {
				return (
					<div className="flex flex-row items-center text-xs font-semibold text-gray-700">
						<Icon className="mr-1 text-gray-700" icon={faInfoCircle} />
						On Demand
					</div>
				);
			}

			// PREC-4470: a finished run that left work undone says so (the caller passes only a
			// run-end note for a DONE list), in the warning tone, instead of a plain "Done".
			if (overwriteText) {
				return (
					<div className="flex flex-row items-center text-xs font-semibold text-status-warning">
						<Icon className="mr-1 text-status-warning" icon={faInfoCircle} />
						{overwriteText}
					</div>
				);
			}

			return (
				<div className="flex flex-row items-center text-xs font-semibold text-status-success">
					<img alt="checkIcon" className="mr-1" src={CheckIcon} />
					{ts(`status.${processingStatus}`)}
				</div>
			);
		case ListProcessingStatus.IDLE: {
			// PREC-3908: read the scheduler's own next run rather than deriving it. The old
			// `lastRunAt + 1 day` was a daily assumption, so a weekly/monthly list was told it
			// runs tomorrow.
			const scheduledLabel = listNextRunLabel(list.nextRunAt);

			// PREC-4121: a run that ended without a result leaves why in `processingStatusMessage`
			// ("No source to process", "Run stopped …"). An idle list carrying one shows the reason
			// instead of its next run, in the warning tone, so it is not mistaken for a healthy
			// schedule — the old chip read "Scheduled for …" over a list nothing would ever fill.
			const restingLabel = overwriteText ?? scheduledLabel;
			const restingIcon = overwriteText ? faInfoCircle : faClock;
			const restingTone = overwriteText ? "text-status-warning" : "text-gray-700";

			const resting = (
				<div className={`flex flex-row items-center text-xs font-semibold ${restingTone}`}>
					<Icon className={`mr-1 ${restingTone}`} icon={restingIcon} />
					{restingLabel}
				</div>
			);

			// PREC-3624: hovering a scheduled list swaps the label to a "Run list now" action that
			// force-runs it immediately instead of waiting for the daily schedule. PREC-4537: the
			// action is offered only where the API would accept it (the same shared decision), and
			// the refusal is the hover text when the control is not offered.
			if (!onRunNow || !list.id) {
				return resting;
			}

			const runNowRefusal = describeRunNowRefusal(list, t);

			if (runNowRefusal) {
				return <Tooltip content={runNowRefusal}>{resting}</Tooltip>;
			}

			return (
				<button
					className={`group/runnow flex cursor-pointer flex-row items-center text-xs font-semibold ${restingTone} hover:text-brand-light`}
					type="button"
					onClick={() => onRunNow(list.id)}
				>
					<span className="inline-flex items-center group-hover/runnow:hidden">
						<Icon className={`mr-1 ${restingTone}`} icon={restingIcon} />
						{restingLabel}
					</span>
					<span className="hidden items-center group-hover/runnow:inline-flex">
						<Icon className="mr-1 text-brand-light" icon={faPlay} />
						Run list now
					</span>
				</button>
			);
		}

		case ListProcessingStatus.WAITING:
		case ListProcessingStatus.WAITING_FOR_SESSION:
			return (
				<div className="flex flex-row items-center text-xs font-semibold text-status-info">
					<img alt="CalendarIcon" className="mr-1" src={CalendarIcon} />
					{ts(`status.${processingStatus}`)}
				</div>
			);
		case ListProcessingStatus.DESKTOP_APP:
			return (
				<div className="flex flex-row items-center text-xs font-semibold text-status-info">
					<Icon className="mr-1 text-gray-700" icon={faClock} />
					{ts(`status.${processingStatus}`)}
				</div>
			);
		default:
			return <></>;
	}

	return <></>;
};
